import Vue from 'vue';
import SidebarRepository from '../../repositories/SidebarRepository';

const storage = window.localStorage;

/**
 * save value to storage with provided key
 *
 * @param {string} key
 * @param {object} value
 * @return void
 */
const saveToStorage = (key, value) => {
	try {
		storage.setItem(key, JSON.stringify(value));
	}
	catch (err) {
		throw Error('Sidebar was updated, but there was an error updating the sidebar cache');
	}
};

export default {
	namespaced: true,
	state: {
		// holds all clients
		clients: {},
		// holds flattened list of groups
		groups: {},
		// holds list of all managed devices
		devices: {},
		// toggle for navigation
		// we will block navigation when we are loading client, group, or device data
		allowNavigation: true
	},
	mutations: {
		SET_CLIENTS: (state, data) => {
			// set items in vuex state
			for (const item of data.list) {
				Vue.set(state.clients, item.id, item);
			}

			// iterate the keys in the store
			for (const key of Object.keys(state.clients)) {
				// if the key does not exist in the list returned by the api, we will remove it
				if (!data.list.find(element => parseInt(element.id) === parseInt(key))) {
					Vue.delete(state.clients, key);
				}
			}

			// if the data came from the api, store it in local storage
			if (data.fromApi) {
				saveToStorage('clients', data.list);
			}
		},
		SET_GROUPS: (state, data) => {
			// set items in vuex state
			for (const item of data.list) {
				Vue.set(state.groups, item.id, item);
			}

			// if the data came from the api, store it in local storage
			if (data.fromApi) {
				saveToStorage('groups', data.list);
			}
		},
		SET_DEVICES: (state, data) => {
			// set items in vuex state
			for (const item of data.list) {
				Vue.set(state.devices, item.id, item);
			}

			// if the data came from the api, store it in local storage
			if (data.fromApi) {
				saveToStorage('devices', data.list);
			}
		},
		ADD_GROUP: (state, data) => {
			Vue.set(state.groups, data.id, data);

			saveToStorage('groups', Object.values(state.groups));
		},
		/**
		 * add list of groups to the app state and store the updated state in storage
		 *
		 * @param state
		 * @param data
		 * @constructor
		 */
		ADD_GROUP_LIST: (state, data) => {
			// iterate the data returned by the api
			for (const item of data.list) {
				// add the data to the list of groups
				Vue.set(state.groups, item.id, item);
			}

			saveToStorage('groups', Object.values(state.groups));
		},
		ADD_DEVICE: (state, data) => {
			Vue.set(state.devices, data.id, data);

			saveToStorage('devices', Object.values(state.devices));
		},
		/**
		 * add list of devices to the app state and store the updated state in storage
		 *
		 * @param state
		 * @param data
		 * @constructor
		 */
		ADD_DEVICE_LIST: (state, data) => {
			// iterate the data returned by the api
			for (const item of data.list) {
				// add the data to the list of devices
				Vue.set(state.devices, item.id, item);
			}

			saveToStorage('devices', Object.values(state.devices));
		},
		UPDATE_GROUP: (state, data) => {
			state.groups[data.id] = data;

			saveToStorage('groups', Object.values(state.groups));
		},
		UPDATE_DEVICE: (state, data) => {
			state.devices[data.id] = data;

			saveToStorage('devices', Object.values(state.devices));
		},
		REMOVE_GROUP: (state, data) => {
			Vue.delete(state.groups, data.id);

			saveToStorage('groups', Object.values(state.groups));
		},
		REMOVE_DEVICE: (state, data) => {
			Vue.delete(state.devices, data.id);

			saveToStorage('devices', Object.values(state.devices));
		},
		CLEAR_STATE: (state, data) => {
			state.clients = {};
			state.groups = {};
			state.devices = {};

			storage.clear();
		},
		/**
		 * turn the ability navigate via the sidebar on/off
		 *
		 * @param state
		 * @param {boolean} value
		 */
		TOGGLE_NAVIGATION: (state, value) => {
			state.allowNavigation = value;
		}
	},
	actions: {
		/**
		 * Get all sidebar data from storage if it exists or pull data from api
		 * This is data initialization for when the sidebar mounts
		 *
		 * @param commit vuex commit function (used for mutations)
		 * @param getters vuex getters object (used to retrieve items from state)
		 * @param {object} request contains client and optional group ex: { client: id, group: id }
		 * @returns {Promise<unknown>}
		 */
		fetchAll ({ commit, getters }, request) {
			// get clients from storage
			const clientString = storage.getItem('clients');
			let promise;

			// if we have any data in storage, we need to determine if we need to pull data from the core
			// we only need to check one item in storage as we save empty lists as well
			if (clientString && clientString.length !== 0) {
				// parse the items that were stored; groups and devices will be pulled, clients are from the retrieval above
				const clientJson = JSON.parse(clientString);
				const groupJson = JSON.parse(storage.getItem('groups'));
				// devices may not have been set yet; if they have not, we initialize to empty array
				const deviceJson = JSON.parse(storage.getItem('devices')) ?? [];

				// initialize whether or not we need to make an api call
				let apiCall = false;

				// get the client, group, and device from respective json objects
				const client = clientJson.find(element => element.id === parseInt(request.client));
				const group = groupJson.find(element => element.id === parseInt(request.group));
				const device = deviceJson.find(element => element.id === parseInt(request.device));

				// if the item is in the request, but not found in the data, we need to retrieve it
				if (request.client && !client) apiCall = true;
				if (request.group && !group) apiCall = true;
				if (request.device && !device) apiCall = true;

				if (!apiCall) {
					// pull items from the local storage and pack into object for mutations
					const clients = { list: clientJson, fromApi: false };
					const groups = { list: groupJson, fromApi: false };
					const devices = { list: deviceJson, fromApi: false };

					// return a promise resolution with proper mutation data
					promise = Promise.resolve({ fromStorage: true, clients: clients, groups: groups, devices: devices });
				}
				else {
					// reach out to the server and get items from the server
					promise = SidebarRepository.fetchAll(request);
				}
			}
			else {
				// reach out to the server and get items from the server
				promise = SidebarRepository.fetchAll(request);
			}

			return promise
				.then(data => {
					let clients = { list: [], fromApi: false };
					let groups = { list: [], fromApi: false };
					let devices = { list: [], fromApi: false };

					if (data.fromStorage) {
						// if the items came from storage, no additional formatting is needed
						clients = data.clients;
						groups = data.groups;
						devices = data.devices;
					}
					else {
						// data came from api, we need to format it for the mutation
						clients = { list: data.clients, fromApi: true };
						groups = { list: data.groups, fromApi: true };
						devices = { list: data.devices, fromApi: true };
					}

					// add the data to the state
					commit('SET_CLIENTS', clients);
					commit('SET_GROUPS', groups);
					commit('SET_DEVICES', devices);

					// resolve promise
					return data;
				});
		},
		/**
		 * Refresh the sidebar to where the user is
		 *
		 * @param commit vuex commit function (used for mutations)
		 * @param getters vuex getters object (used to retrieve data from state)
		 * @param {object} request contains a client and optional group ex: { client: id, group: id }
		 * @returns {Promise<unknown>}
		 */
		refresh ({ commit, getters }, request) {
			// clear the current sidebar state
			commit('CLEAR_STATE');

			// fetch data from the core
			return SidebarRepository.fetchAll(request)
				.then(data => {
					// format the data for mutation
					const clients = { list: data.clients, fromApi: true };
					const groups = { list: data.groups, fromApi: true };
					const devices = { list: data.devices, fromApi: true };

					// add data to the state
					commit('SET_CLIENTS', clients);
					commit('SET_GROUPS', groups);
					commit('SET_DEVICES', devices);

					// resolve the promise
					return data;
				});
		},
		/**
		 * retrieve data for a particular client from the core
		 *
		 * @param commit vuex commit function (used for mutations)
		 * @param getters vuex getters object (used to retrieve information from state)
		 * @param {object} request contains data pertaining to api request and data used by the action: { client: id }
		 * @returns {Promise<unknown>}
		 */
		fetchClient ({ commit, getters }, request) {
			// fetch data from the core
			return SidebarRepository.fetchAll(request)
				.then(data => {
					// format the data for mutation
					const groups = { list: data.groups, fromApi: true };
					const clients = { list: data.clients, fromApi: true };

					// if clear_data is defined and truthy we will clear the
					// the existing sidebar data before repopulating
					if (request.clear_data) {
						commit('CLEAR_STATE');
					}

					// add data to the state
					commit('ADD_GROUP_LIST', groups);
					commit('SET_CLIENTS', clients);

					// resolve the promise
					return data;
				});
		},
		/**
		 * retrieve data for a particular group from the core
		 *
		 * @param commit vuex commit function (used for mutations)
		 * @param getters vuex getters object (used to retrieve information from state)
		 * @param {object} request contains a client and group ex: { client: id, group: id }
		 * @returns {Promise<unknown>}
		 */
		fetchGroup ({ commit, getters }, request) {
			// fetch data from the core
			return SidebarRepository.fetchAll(request)
				.then(data => {
					// format the data for mutation
					const groups = { list: data.groups, fromApi: true };
					const devices = { list: data.devices, fromApi: true };

					// if clear_data is defined and truthy we will clear the
					// the existing sidebar data before repopulating
					if (request.clear_data) {
						commit('CLEAR_STATE');

						// when we clear the data, we have to set the client data
						const clients = { list: data.clients, fromApi: true };
						commit('SET_CLIENTS', clients);
					}

					// add data to the state
					commit('ADD_GROUP_LIST', groups);
					commit('ADD_DEVICE_LIST', devices);

					// resolve the promise
					return data;
				});
		},
		storageClear ({ commit }) {
			commit('CLEAR_STATE');

			return Promise.resolve(true);
		}
	},
	getters: {
		getClients: (state) => {
			return Object.values(state.clients);
		},
		getGroups: (state) => {
			return Object.values(state.groups);
		},
		getDevices: (state) => {
			return Object.values(state.devices);
		},
		/**
		 * get a client from the state based on id
		 *
		 * @param state
		 * @returns {function(*=): *}
		 */
		getClient: (state) => (id) => {
			return state.clients[parseInt(id)];
		},
		/**
		 * get a group from the state based on id
		 *
		 * @param state
		 * @returns {function(*=): *}
		 */
		getGroup: (state) => (id) => {
			return state.groups[parseInt(id)];
		},
		/**
		 * get a device from the state based on id
		 *
		 * @param state
		 * @returns {function(*=): *}
		 */
		getDevice: (state) => (id) => {
			return state.device[parseInt(id)];
		},
		/**
		 * get if we should allow navigation via the sidebar
		 *
		 * @param state
		 * @returns {boolean}
		 */
		getAllowNavigation: (state) => {
			return state.allowNavigation;
		},
		/**
		 * get the count of groups for a client
		 *
		 * @param state
		 * @returns {function(*=): number}
		 */
		getClientGroupCount: (state) => (id) => {
			return Object.values(state.groups).filter(group => group.client_id === parseInt(id)).length;
		},

		getDefaultGroupsLimitForSidebar: () => {
			return (
				parseInt(process.env.VUE_APP_DEFAULT_SIDEBAR_GROUPS_LIMIT) ||
				20
			);
		},
		getFilteredGroupsLimitForSidebar: () => {
			return (
				parseInt(process.env.VUE_APP_FILTERED_SIDEBAR_GROUPS_LIMIT) ||
				100
			);
		},
		getMinCharactersToSearch: () => {
			return parseInt(process.env.VUE_APP_MIN_CHARACTERS_TO_SEARCH) || 2;
		}
	}
};
